import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import gsap from 'gsap';
import { useAuth } from '../context/AuthContext';
import Toast from './Toast';
// Light-colored logo variant — reads correctly against this page's dark background.
const autoboyLogo = '/autoboy_logo3.png';
import sellerAuthVisual from '../assets/auth/seller-auth-visual.png';

const TRUST_CHIPS = [
  { icon: 'fa-store', label: '2K+ Sellers' },
  { icon: 'fa-sack-dollar', label: '₦500M+ Transactions' },
  { icon: 'fa-truck-fast', label: '99.2% Delivery Rate' },
];

const SellerLogin = () => {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [showPassword, setShowPassword] = useState(false);
  const [toast, setToast] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [errors, setErrors] = useState({});

  const visualRef = useRef(null);
  const formWrapperRef = useRef(null);

  useEffect(() => {
    gsap.fromTo(
      formWrapperRef.current,
      { opacity: 0, y: 18 },
      { opacity: 1, y: 0, duration: 0.55, ease: 'power3.out' }
    );
  }, []);

  useEffect(() => {
    const handleMouseMove = (e) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 24;
      const y = (e.clientY / window.innerHeight - 0.5) * 24;
      gsap.to(visualRef.current, { x, y, duration: 1.2, ease: 'power2.out' });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = 'Please enter your email';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!formData.password) {
      newErrors.password = 'Please enter your password';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);

    try {
      const response = await login({
        email: formData.email,
        password: formData.password
      });

      const userData = response?.data?.user || response?.user;
      const userType = userData?.user_type || userData?.type;

      if (userType !== 'seller') {
        setToast({
          message: 'This account is not registered as a seller. Please use buyer login.',
          type: 'error'
        });
        setIsLoading(false);
        return;
      }

      if (response?.data?.requires_2fa || response?.requires_2fa) {
        setToast({
          message: '2FA required. Please check your SMS/Email for the verification code.',
          type: 'info'
        });
        setIsLoading(false);
        return;
      }

      setToast({
        message: 'Login successful! Welcome back.',
        type: 'success'
      });

      setTimeout(() => {
        navigate('/homepage');
      }, 1500);

    } catch (error) {
      setToast({
        message: error.message || 'Login failed. Please check your credentials.',
        type: 'error'
      });
      setIsLoading(false);
    }
  };

  return (
    <div className="tw-min-h-dvh tw-w-full tw-bg-[#0A0A0A] tw-flex tw-flex-col lg:tw-flex-row tw-font-['Epilogue',_sans-serif]">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* Left — Brand Visual */}
      <div className="tw-relative tw-hidden lg:tw-flex lg:tw-w-[46%] xl:tw-w-1/2 tw-flex-col tw-justify-between tw-overflow-hidden tw-bg-black">
        <img
          ref={visualRef}
          src={sellerAuthVisual}
          alt=""
          aria-hidden="true"
          className="tw-absolute tw-inset-0 tw-h-[110%] tw-w-[110%] -tw-top-[5%] -tw-left-[5%] tw-object-cover tw-scale-105"
        />
        <div className="tw-absolute tw-inset-0 tw-bg-gradient-to-t tw-from-black tw-via-black/60 tw-to-black/20" />
        <div className="tw-absolute tw-inset-0 tw-bg-gradient-to-r tw-from-black/40 tw-via-transparent tw-to-transparent" />

        <div className="tw-relative tw-z-10 tw-p-10 xl:tw-p-14">
          <a href="/homepage" className="tw-inline-flex tw-items-center tw-gap-2 tw-text-white/90 hover:tw-text-white tw-transition-colors tw-text-sm tw-font-medium">
            <i className="fas fa-arrow-left tw-text-xs" />
            Back to shop
          </a>
        </div>

        <div className="tw-relative tw-z-10 tw-p-10 xl:tw-p-14 tw-pb-14 xl:tw-pb-16">
          <img src={autoboyLogo} alt="AutoboyExpress" className="tw-h-36 xl:tw-h-44 tw-w-auto tw-mb-8 tw-drop-shadow-[0_2px_28px_rgba(34,197,94,0.45)]" />
          <h1 className="tw-text-[2.5rem] xl:tw-text-[3rem] tw-font-extrabold tw-leading-[1.05] tw-tracking-tight tw-text-white tw-mb-4">
            Turn your inventory<br />into
            <span className="tw-bg-gradient-to-r tw-from-green-400 tw-to-green-500 tw-bg-clip-text tw-text-transparent"> income.</span>
          </h1>
          <p className="tw-text-white/60 tw-text-base tw-max-w-md tw-mb-9 tw-leading-relaxed">
            List your products, reach thousands of buyers, and get paid securely into your bank account.
          </p>
          <div className="tw-flex tw-flex-wrap tw-gap-3">
            {TRUST_CHIPS.map((chip) => (
              <div
                key={chip.label}
                className="tw-flex tw-items-center tw-gap-2 tw-rounded-full tw-border tw-border-white/10 tw-bg-white/[0.06] tw-backdrop-blur-xl tw-px-4 tw-py-2 tw-text-xs tw-font-semibold tw-text-white/85"
              >
                <i className={`fas ${chip.icon} tw-text-green-400`} />
                {chip.label}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right — Form */}
      <div className="tw-flex tw-flex-1 tw-flex-col tw-items-center tw-justify-center tw-bg-[#0A0A0A] tw-px-6 tw-py-10 sm:tw-px-10 lg:tw-px-16 tw-min-h-dvh tw-overflow-y-auto">
        <div className="tw-flex lg:tw-hidden tw-w-full tw-max-w-[420px] tw-items-center tw-justify-between tw-mb-8">
          <a href="/homepage" className="tw-inline-flex tw-items-center tw-gap-2 tw-text-white/60 hover:tw-text-white tw-transition-colors tw-text-sm tw-font-medium">
            <i className="fas fa-arrow-left tw-text-xs" />
            Back
          </a>
          <img src={autoboyLogo} alt="AutoboyExpress" className="tw-h-20 tw-w-auto" />
        </div>

        <div ref={formWrapperRef} className="tw-w-full tw-max-w-[420px] tw-py-6">
          <img src={autoboyLogo} alt="AutoboyExpress" className="tw-hidden lg:tw-block tw-h-28 tw-w-auto tw-mb-6 tw-mx-auto" />

          <div className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-border tw-border-green-500/20 tw-bg-green-500/10 tw-px-3 tw-py-1 tw-text-xs tw-font-bold tw-text-green-400 tw-mb-4">
            <i className="fas fa-store tw-text-[0.65rem]" />
            SELLER PORTAL
          </div>
          <h2 className="tw-text-3xl sm:tw-text-4xl tw-font-bold tw-text-white tw-mb-1.5 tw-tracking-tight">
            Welcome back
          </h2>
          <p className="tw-text-white/45 tw-text-sm tw-mb-8">
            Log in to manage your store, orders, and payouts.
          </p>

          <form className="tw-flex tw-flex-col tw-gap-5" onSubmit={handleSubmit}>
            <div>
              <label className="tw-block tw-text-sm tw-font-medium tw-text-white/80 tw-mb-2">Email Address</label>
              <div className="tw-relative">
                <i className="fas fa-envelope tw-absolute tw-left-4 tw-top-1/2 -tw-translate-y-1/2 tw-text-white/25 tw-text-sm" />
                <input
                  type="email"
                  className="tw-w-full tw-rounded-xl tw-border tw-border-white/10 tw-bg-white/[0.04] tw-pl-11 tw-pr-4 tw-py-3.5 tw-text-white tw-placeholder-white/25 tw-outline-none tw-transition-all focus:tw-border-green-500 focus:tw-bg-white/[0.06] focus:tw-ring-4 focus:tw-ring-green-500/10"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                />
              </div>
              {errors.email && <p className="tw-mt-1.5 tw-text-xs tw-font-medium tw-text-red-400">{errors.email}</p>}
            </div>

            <div>
              <label className="tw-block tw-text-sm tw-font-medium tw-text-white/80 tw-mb-2">Password</label>
              <div className="tw-relative">
                <i className="fas fa-lock tw-absolute tw-left-4 tw-top-1/2 -tw-translate-y-1/2 tw-text-white/25 tw-text-sm" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  className="tw-w-full tw-rounded-xl tw-border tw-border-white/10 tw-bg-white/[0.04] tw-pl-11 tw-pr-11 tw-py-3.5 tw-text-white tw-placeholder-white/25 tw-outline-none tw-transition-all focus:tw-border-green-500 focus:tw-bg-white/[0.06] focus:tw-ring-4 focus:tw-ring-green-500/10"
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                />
                <button
                  type="button"
                  className="tw-absolute tw-right-4 tw-top-1/2 -tw-translate-y-1/2 tw-text-white/35 hover:tw-text-green-400 tw-transition-colors"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                </button>
              </div>
              {errors.password && <p className="tw-mt-1.5 tw-text-xs tw-font-medium tw-text-red-400">{errors.password}</p>}
            </div>

            <label className="tw-flex tw-items-center tw-gap-2 tw-cursor-pointer">
              <input
                type="checkbox"
                checked={formData.rememberMe}
                onChange={(e) => handleInputChange('rememberMe', e.target.checked)}
                className="tw-h-4 tw-w-4 tw-rounded tw-accent-green-500 tw-cursor-pointer"
              />
              <span className="tw-text-sm tw-text-white/60">Remember me</span>
            </label>

            <button
              type="submit"
              disabled={isLoading}
              className="tw-mt-1 tw-w-full tw-rounded-xl tw-bg-gradient-to-r tw-from-green-500 tw-to-green-600 tw-py-3.5 tw-text-base tw-font-bold tw-text-white tw-shadow-lg tw-shadow-green-500/20 tw-transition-all hover:-tw-translate-y-0.5 hover:tw-shadow-xl hover:tw-shadow-green-500/30 active:tw-translate-y-0 active:tw-scale-[0.98] disabled:tw-opacity-60 disabled:tw-pointer-events-none"
            >
              {isLoading ? 'Logging in...' : 'Login'}
            </button>
          </form>

          <p className="tw-text-center tw-text-sm tw-text-white/45 tw-mt-8">
            Don't have a seller account?{' '}
            <a href="/seller-signup" className="tw-font-semibold tw-text-green-400 hover:tw-text-green-300 tw-transition-colors">
              Sign Up
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SellerLogin;
